import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { AccountBalanceWalletRounded, LinkRounded, LinkOffRounded } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../../stores/tenant.store';
import { mercadopagoApi, type PaymentIntegrationStateDto } from '../../lib/api/mercadopago.api';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { errorText } from '../../lib/error-text';
import { formatDateTime } from '../../lib/i18n-format';

export function MercadoPagoIntegrationPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const stateQ = useQuery({
    queryKey: ['mercadopago-integration-state', currentTenantId],
    enabled: !!currentTenantId,
    queryFn: () => mercadopagoApi.getState({ tenantId: currentTenantId! }),
  });

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Surface the result of the OAuth redirect, then clean the URL.
  useEffect(() => {
    if (searchParams.get('mpConnected') === '1') {
      setInfo(t('mpIntegration.banner.connected'));
      qc.invalidateQueries({ queryKey: ['mercadopago-integration-state'] });
    }
    const mpError = searchParams.get('mpError');
    if (mpError) setError(t('mpIntegration.banner.error', { reason: decodeURIComponent(mpError) }));
    if (searchParams.has('mpConnected') || searchParams.has('mpError')) {
      searchParams.delete('mpConnected');
      searchParams.delete('mpError');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    if (!currentTenantId) return;
    setConnecting(true);
    setError(null);
    try {
      const { url } = await mercadopagoApi.getConnectUrl({
        tenantId: currentTenantId,
        returnOrigin: window.location.origin,
      });
      window.location.href = url;
    } catch (e) {
      setError(errorText(e, t('mpIntegration.errors.connect')));
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!currentTenantId) return;
    setDisconnecting(true);
    setError(null);
    setInfo(null);
    try {
      await mercadopagoApi.disconnect({ tenantId: currentTenantId });
      setInfo(t('mpIntegration.banner.disconnected'));
      qc.invalidateQueries({ queryKey: ['mercadopago-integration-state'] });
      setConfirmOpen(false);
    } catch (e) {
      setError(errorText(e, t('mpIntegration.errors.disconnect')));
    } finally {
      setDisconnecting(false);
    }
  };

  const state = stateQ.data;

  return (
    <Box sx={{ maxWidth: 820, p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <AccountBalanceWalletRounded sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('mpIntegration.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('mpIntegration.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('mpIntegration.account.heading')}
          </Typography>
          <MercadoPagoStatusChip state={state} loading={stateQ.isLoading} t={t} />
        </Stack>

        {stateQ.isLoading ? (
          // Avoid flashing the "not connected" state while the query resolves.
          <Stack alignItems="center" py={3}>
            <CircularProgress size={22} />
          </Stack>
        ) : (
          <>
            {state?.connected ? (
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {t('mpIntegration.account.connectedAs', {
                    name: state.nickname ?? state.email ?? state.mpUserId ?? '—',
                  })}
                </Typography>
                {state.connectedAt && (
                  <Typography variant="caption" color="text.secondary">
                    {t('mpIntegration.account.connectedAt', {
                      date: formatDateTime(state.connectedAt),
                    })}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('mpIntegration.account.notConnected')}
              </Typography>
            )}

            {state?.lastError && state.status === 'error' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('mpIntegration.account.lastError', { error: state.lastError })}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                startIcon={
                  connecting ? <CircularProgress size={14} color="inherit" /> : <LinkRounded />
                }
                onClick={connect}
                disabled={connecting || !currentTenantId}
              >
                {state?.connected
                  ? t('mpIntegration.actions.reconnect')
                  : t('mpIntegration.actions.connect')}
              </Button>
              {state?.connected && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={
                    disconnecting ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <LinkOffRounded />
                    )
                  }
                  onClick={() => setConfirmOpen(true)}
                  disabled={disconnecting}
                >
                  {t('mpIntegration.actions.disconnect')}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Card>

      <Card sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          {t('mpIntegration.how.heading')}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          <ol style={{ paddingLeft: 20, marginTop: 8 }}>
            <li>{t('mpIntegration.how.step1')}</li>
            <li>{t('mpIntegration.how.step2')}</li>
            <li>{t('mpIntegration.how.step3')}</li>
          </ol>
        </Typography>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title={t('mpIntegration.disconnectTitle')}
        message={t('mpIntegration.confirmDisconnect')}
        confirmLabel={t('mpIntegration.actions.disconnect')}
        cancelLabel={t('common:actions.cancel')}
        confirmColor="error"
        loading={disconnecting}
        onConfirm={disconnect}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
}

function MercadoPagoStatusChip({
  state,
  loading,
  t,
}: {
  state?: PaymentIntegrationStateDto | undefined;
  loading?: boolean | undefined;
  t: (key: string) => string;
}): JSX.Element {
  if (loading) return <CircularProgress size={16} />;
  if (!state || !state.connected) {
    if (state?.status === 'error') {
      return <Chip size="small" label={t('mpIntegration.status.error')} color="error" />;
    }
    return <Chip size="small" label={t('mpIntegration.status.notConnected')} variant="outlined" />;
  }
  return <Chip size="small" label={t('mpIntegration.status.connected')} color="success" />;
}
